import React, { useState, useEffect, ReactNode } from 'react';
import {
    ApolloClient,
    ApolloLink,
    ApolloProvider,
    HttpLink,
    NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import LogRocket from 'logrocket';

import { resolvers, typeDefs } from '@trqk/resolvers';
import {
    getCognitoToken,
    getWebpackEnvVar,
    shouldEnableLogRocket,
} from '@trqk/utils';

import { redirectOnMissingToken } from '../../utils';
import { useToast } from '../../hooks';
import { ApolloErrorCard } from '../../components/toastMessages';
import { cache, initialStateData, INITIAL_STATE } from '..';
import { PATHS } from '../../constants';
import { getFeatureFlags } from '../../featureFlags';

interface ApolloWrapperProps {
    children: ReactNode;
}

const enableLogRocket = shouldEnableLogRocket();

export const ApolloWrapper = ({
    children,
}: ApolloWrapperProps): JSX.Element | null => {
    const userServiceUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const [tokenRefreshFailed, setTokenRefreshFailed] = useState(false);
    // Haven't figured out how to appropriately define the generic TCacheShape for ApolloClient yet
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [client, setClient] = useState<undefined | ApolloClient<any>>(
        undefined,
    );

    const toast = useToast();
    const { apolloClientErrorDialog } = getFeatureFlags();

    useEffect(() => {
        if (!client && toast) {
            cache.writeQuery({
                query: INITIAL_STATE,
                data: initialStateData,
            });

            const httpLink = new HttpLink({ uri: userServiceUrl as string });

            const authLink = setContext(async (request, { headers }) => {
                const { error, token } = await getCognitoToken({
                    clientId,
                    userPoolId,
                });

                if (error || !token) {
                    setTokenRefreshFailed(true);

                    return headers;
                }

                return {
                    headers: {
                        ...headers,
                        authorization: token,
                    },
                };
            });

            const errorLink = onError(errorResponse => {
                if (apolloClientErrorDialog) {
                    // If `apolloClientErrorDialog` featureFlag is `true`, show error details in a
                    // toast that persists until the user closes it
                    toast.add({
                        kind: 'info-card',
                        content: (
                            <ApolloErrorCard errorResponse={errorResponse} />
                        ),
                        persist: true,
                    });
                }

                const { response, networkError, graphQLErrors } = errorResponse;

                if (
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- the `errorType` is present for 401 Unauthorized Exception errors on the AppSync endpoint
                    // @ts-ignore
                    graphQLErrors?.[0]?.errorType !== 'UnauthorizedException' &&
                    enableLogRocket
                ) {
                    if (networkError && !graphQLErrors) {
                        LogRocket.captureException(networkError);
                    } else if (graphQLErrors) {
                        const firstPath =
                            graphQLErrors?.[0]?.path?.[0]?.toString() ?? null;

                        LogRocket.captureException(
                            new Error(firstPath ?? 'GraphQLErrors'),
                            {
                                extra: {
                                    graphQLErrors:
                                        JSON.stringify(graphQLErrors),
                                    networkError: JSON.stringify(networkError),
                                },
                            },
                        );
                    }
                }

                // Prevent ungraceful failures by ignoring errors and setting data to empty object
                if (response) {
                    response.errors = undefined;
                    response.data = {};
                }
            });

            const link = ApolloLink.from([authLink, errorLink, httpLink]);

            const newClient = new ApolloClient<NormalizedCacheObject>({
                cache,
                link,
                resolvers,
                typeDefs,
            });

            newClient.onResetStore(async () =>
                cache.writeQuery({
                    query: INITIAL_STATE,
                    data: initialStateData,
                }),
            );

            setClient(newClient);

            return (): void => {
                newClient.resetStore();
            };
        }

        return undefined;
    }, [client, toast]);

    if (tokenRefreshFailed) {
        setTokenRefreshFailed(false);

        if (
            ![PATHS.SIGN_IN, PATHS.SIGN_UP].includes(window.location.pathname)
        ) {
            redirectOnMissingToken();
        }
    }

    if (client === undefined) {
        return null;
    }

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
